import Big from 'big.js';
import { AxiosError } from 'smg/axios';
import Batch from 'smg/batch';
import { AddCartItemParams, GetCartID, UpdateCartItems } from 'smg/customer-api/v2/carts';
import { ChildLineItemModelByID, ChildLineItemOptionModelByID, LineItemModelByID, LineItemTypeModelByID, LineItemTypeSizeModelByID } from 'smg/customer-api/lineitems';
import * as models from 'smg/customer-api/models';
import { ShowError } from 'smg/errors';
import * as filepath from 'smg/filepath';
import * as html from 'smg/html';
import Modal from 'smg/modal';
import QueryString from 'smg/querystring';
import * as strings from 'smg/strings';
import * as template from 'smg/template';
import * as toast from 'smg/toast';
import * as uploads from 'smg/uploads';
import { DollarFormat } from 'smg/util';
import { SourceOrder } from 'smg/cart-items';
import * as carts from '../carts';
import { BuilderPath, IsNoMinimumBuilderSKU } from '../builders';
import { Relref } from '../hugo';
import * as lineitems from '../lineitems';
import { PromptQuantityChange } from '../quote';

declare interface myWindow extends Window {
    readonly cloudFront: string;
}
declare const window: myWindow;

// declare interface quantityOverride {
//     cartItemID: string;
//     objectLineItemID: string;
//     quantity: number;
// }

void main();

async function main() {
    const errorId = document.querySelector('#error-div') as HTMLElement;

    const quoteID = QueryString().get('quote');
    if (quoteID) {
        await carts.AddCartItem({ quoteID }, {
            promptToCheckout: false,
            hideCartLink: true,
        }).catch(() => errorId.classList.remove('d-none'));
    }
    const orderID = QueryString().get('order');
    if (orderID) {
        await carts.AddCartItem({ orderID }, {
            promptToCheckout: false,
            hideCartLink: true,
        }).catch(() => errorId.classList.remove('d-none'));
    }
    const newCartItem = QueryString().get('new');
    if (newCartItem) {
        try {
            await carts.AddCartItem(JSON.parse(newCartItem) as AddCartItemParams, {
                promptToCheckout: false,
            });
        } catch (e) {
            ShowError(e);
        }
    }
    const cartID = QueryString().get('cart');
    if (cartID) {
        await carts.AddCartItem({ cartID }, {
            promptToCheckout: false,
            hideCartLink: true,
        }).catch(() => errorId.classList.remove('d-none'));
    }

    // const lineItemPriceBreakClickedIDs: string[] = [];

    const cartCard = document.querySelector('#cart-card') as HTMLElement;

    const spinner = document.querySelector('#cart-spinner') as HTMLElement;
    const checkoutButton = document.querySelector('#checkout-button') as HTMLElement;

    const cartEmpty = document.querySelector('#cart-empty') as HTMLElement;
    cartEmpty.parentElement?.removeChild(cartEmpty);
    cartEmpty.classList.remove('d-none');

    const showEmptyCart = () => {
        cartCard.innerHTML = '';
        cartCard.appendChild(cartEmpty);

        const quoteSearchBtn = document.querySelector('#quote-search-btn') as HTMLAnchorElement;
        quoteSearchBtn.classList.add('btn', 'btn-primary', 'text-white');
        quoteSearchBtn.parentElement?.classList.remove('justify-content-start');
        quoteSearchBtn.parentElement?.classList.add('justify-content-center');
        quoteSearchBtn.innerHTML = '<i class="fas fa-search"></i> Search for your quotes.';

        checkoutButton.setAttribute('disabled', 'disabled');
    };

    const $cartItemTemplate = document.querySelector('#cart-item-template') as HTMLTemplateElement;
    const $cartItemStockItemTemplate = document.querySelector('#cart-item-stock-item-template') as HTMLTemplateElement;
    const $cartItemUploadTemplate = document.querySelector('#cart-item-upload-template') as HTMLTemplateElement;
    // Order lines with a quantity edit in flight (prompting → saving → refreshing),
    // keyed "<cartItemID>:<lineItemID>" so the lock survives a redraw.
    const editingLines = new Set<string>();

    const drawCartItem = async (cartItem: models.CartItem): Promise<{ $: HTMLElement; p?: Promise<unknown>; invalidCustomQuantity: boolean }> => {
        let $template = $cartItemTemplate;
        if (cartItem.stockItem) {
            $template = $cartItemStockItemTemplate;
        } else if (cartItem.upload) {
            $template = $cartItemUploadTemplate;
        }

        const $cartItem = template.Clone($template).firstElementChild as HTMLElement;
        $cartItem.removeAttribute('id');
        const promises: Promise<unknown>[] = [];
        const title = $cartItem.querySelector('.cart-item-title') as HTMLElement;
        const soldOrder = SourceOrder(cartItem);

        const subtotal = $cartItem.querySelector('.cart-item-subtotal') as HTMLElement;
        subtotal.textContent = DollarFormat(cartItem.subtotal || 0);

        const oldSubtotal = $cartItem.querySelector('.text-decoration-line-through') as HTMLElement;
        if (cartItem.maxNetworkSubtotal && Big(cartItem.maxNetworkSubtotal).gt(cartItem.subtotal || 0)) {
            subtotal.classList.add('text-primary');
            oldSubtotal.textContent = DollarFormat(cartItem.maxNetworkSubtotal);
        } else {
            oldSubtotal.parentElement?.removeChild(oldSubtotal);
        }

        const deleteLink = $cartItem.querySelector('.cart-item-delete') as HTMLAnchorElement;
        deleteLink.addEventListener('click', e => {
            e.preventDefault();

            let hr: HTMLElement | undefined;
            if ($cartItem.nextElementSibling?.nodeName.toLowerCase() === 'hr') {
                hr = $cartItem.nextElementSibling as HTMLElement;
            } else if ($cartItem.previousElementSibling?.nodeName.toLowerCase() === 'hr') {
                hr = $cartItem.previousElementSibling as HTMLElement;
            }

            const els = [$cartItem];
            if (hr) {
                els.push(hr);
            }

            els.forEach(el => el.classList.add('d-none'));
            carts.DeleteCartItem(cartItem.id || '', drawCart).
                then(() => {
                    void drawCart();
                    els.forEach(el => el.parentElement?.removeChild(el));
                    if (!carts.GetCurrentCartCount()) {
                        showEmptyCart();
                    }
                }).
                catch(err => {
                    els.forEach(el => el.classList.remove('d-none'));
                    throw err;
                });
        });

        const revisionParent = $cartItem.querySelector<HTMLElement>('.cart-item-revision-parent');
        const revision = revisionParent?.querySelector<HTMLAnchorElement>('.cart-item-revision');
        if (cartItem.quote?.id && revision) {
            revision.href = `${Relref('/revisions')}?id=${cartItem.quote.key || ''}`;
        } else if (revisionParent) {
            revisionParent.parentElement?.removeChild(revisionParent);
        }

        const info = $cartItem.querySelector<HTMLAnchorElement>('.cart-item-info');
        if (cartItem.quote?.quoteLineItems?.length || soldOrder?.orderLineItems?.length || cartItem.uploadLineItem) {
            info?.addEventListener('click', async e => {
                e.preventDefault();

                const entityLineItem: (models.QuoteLineItem | models.OrderLineItem)[] = [];
                if (cartItem.quote?.quoteLineItems?.length) {
                    entityLineItem.push(...cartItem.quote.quoteLineItems);
                } else if (soldOrder?.orderLineItems?.length) {
                    entityLineItem.push(...soldOrder.orderLineItems);
                } else if (cartItem.uploadLineItem) {
                    entityLineItem.push(...uploads.CartItemOrderLineItems(cartItem));
                }

                new Modal({
                    title: `Pricing breakdown for ${title.textContent || ''}`,
                    body: await lineitems.Table(entityLineItem),
                    size: 'modal-lg',
                    cancelText: 'Close',
                });
            });
        } else if (info) {
            info.parentElement?.removeChild(info);
        }
        const drawCartItemLine = async (lineItem: models.QuoteLineItem | models.OrderLineItem): Promise<{ $: HTMLElement; p?: Promise<void> }> => {
            lineItem.lineItemModel ??= await LineItemModelByID(lineItem.lineItemID || '');
            lineItem.lineItemType ??= await LineItemTypeModelByID(lineItem.lineItemTypeID || '');
            lineItem.lineItemTypeSize ??= await LineItemTypeSizeModelByID(lineItem.lineItemTypeSizeID || '');
            for (const childLineItem of lineItem instanceof models.QuoteLineItem ? lineItem.quoteChildLineItems : lineItem.orderChildLineItems) {
                childLineItem.childLineItem ??= await ChildLineItemModelByID(childLineItem.childLineItemID || '');
                childLineItem.childLineItemOption ??= await ChildLineItemOptionModelByID(childLineItem.childLineItemOptionID || '');
            }

            const $lineItem = template.Clone('#cart-item-line', $cartItem).firstElementChild as HTMLElement;
            const summary = $lineItem.querySelector('.cart-item-summary') as HTMLElement;

            const $proofLink = $lineItem.querySelector('.proof-link') as HTMLAnchorElement;
            const $proofImg = $proofLink.querySelector('img') as HTMLImageElement;
            const proof = lineItem instanceof models.QuoteLineItem ? lineItem.quoteProof?.proof : lineItem.orderProof?.proof;
            let proofName: string = '';
            if (proof?.main) {
                $proofLink.href = proof.main;
                $proofLink.setAttribute('data-fancybox', `cart-item-${cartItem.id || ''}`);

                $proofImg.src = proof.thumbnail || proof.main;

                if (lineItem instanceof models.QuoteLineItem && lineItem.quoteProof?.number) {
                    proofName = `Proof ${lineItem.quoteProof.number || ''}`;
                } else if (lineItem instanceof models.OrderLineItem) {
                    proofName = lineItem.orderProof?.name || '';
                }
                $proofLink.setAttribute('data-caption', proofName);

                $proofImg.title = proofName;
                $proofImg.alt = proofName;
            } else {
                $proofLink.parentElement?.removeChild($proofLink);
            }

            summary.textContent = '';
            if (proofName) {
                summary.textContent += proofName;
                summary.textContent += ' - ';
            }

            const qty = lineItem.quantity || 0;
            const product = (qty === 1 ? lineItem.lineItemModel?.networkName() : lineItem.lineItemModel?.networkNamePlural()) || lineItem.lineItem;
            summary.textContent += `${Number(qty).toLocaleString()} ${product || ''}`;

            const controls = $lineItem.querySelector('.cart-item-line-controls') as HTMLElement;
            const changeQty = controls.querySelector('.cart-item-edit-quantity') as HTMLAnchorElement;
            const removeParent = controls.querySelector('.cart-item-line-delete-parent') as HTMLElement;
            const remove = removeParent.querySelector('.cart-item-line-delete') as HTMLAnchorElement;

            if (!lineItem.lineItemModel?.product) {
                controls.parentElement?.removeChild(controls);
            } else {
                if (productCount < 2) {
                    removeParent.parentElement?.removeChild(removeParent);
                }

                // A customer-editable line with no advertised tiers is a $0 product line; the editor cannot price what the network never priced.
                if (lineItem instanceof models.OrderLineItem && lineItem.customerEditable && lineItem.quantityTiers.length) {
                    // One edit at a time per line: from the click until the cart is
                    // redrawn (prompting → saving → refreshing), later clicks are ignored.
                    // Keyed by line, not by this element — a redraw while the modal is
                    // open replaces the element but not the edit in flight.
                    const editKey = `${cartItem.id || ''}:${lineItem.id || ''}`;
                    changeQty.addEventListener('click', async e => {
                        e.preventDefault();
                        if (editingLines.has(editKey)) {
                            return;
                        }
                        editingLines.add(editKey);

                        try {
                            const oldQty = lineItem.quantity || 1;

                            let newQty: number;
                            try {
                                newQty = await carts.PromptQuantityEdit(oldQty, lineItem.quantityTiers);
                            } catch (err) {
                                if (err === carts.QuantityEditCancelled) {
                                    return;
                                }
                                throw err;
                            }
                            if (newQty === oldQty) {
                                return;
                            }

                            await UpdateCartItems([{ id: cartItem.id || '', lineItems: [{ id: lineItem.id || '', quantity: newQty }] }]);
                            if (await drawCart()) {
                                toast.Success('Quantity updated');
                            } else {
                                toast.Warning('Quantity saved, but the cart could not be refreshed. Reload the page to see the new total.');
                            }
                        } catch (err) {
                            ShowError(err);
                        } finally {
                            editingLines.delete(editKey);
                        }
                    });
                } else {
                    changeQty.addEventListener('click', e => {
                        e.preventDefault();
                        PromptQuantityChange({ quote: cartItem.quote, order: soldOrder, lineItem }).then(() => {
                            void drawCart();
                            toast.Success('Request Sent!');
                        }).catch(err => toast.Error(String(err)));
                    });
                }

                remove.addEventListener('click', e => {
                    e.preventDefault();

                    const hide = () => $lineItem.classList.add('d-none');
                    const show = () => $lineItem.classList.remove('d-none');

                    const deleteItem = async () => {
                        hide();
                        return UpdateCartItems([{ id: cartItem.id || '', lineItems: [{ id: lineItem.id || '', active: false }] }]).
                            then(() => {
                                void drawCart();

                                toast.Undo('Removed from cart.', () => restore());
                            }).
                            catch(err => {
                                toast.Error('Something went wrong!');
                                show();
                                throw err;
                            });
                    };

                    const restore = async () => {
                        show();
                        return UpdateCartItems([{ id: cartItem.id || '', lineItems: [{ id: lineItem.id || '', active: true }] }]).then(() => {
                            void drawCart();
                            toast.Success('Restored item.');
                        }).catch(err => {
                            hide();
                            ShowError(err);
                        });
                    };

                    void deleteItem();
                });
            }

            if (lineItem.invalidCustomQuantity) {
                $lineItem.querySelector<HTMLElement>('.cart-item-invalid-quantity')?.classList.remove('d-none');

                const lowQtyRequest = $lineItem.querySelector('.request-low-qty-pricing') as HTMLAnchorElement;
                lowQtyRequest.addEventListener('click', e => {
                    e.preventDefault();

                    PromptQuantityChange({ quote: cartItem.quote, order: soldOrder, lineItem }).then(() => {
                        void drawCart();
                        toast.Success('Request Sent!');
                    }).catch(err => toast.Error(String(err)));
                });
            }
            return { $: $lineItem };
        };

        const $cartItemDescription = $cartItem.querySelector('.cart-item-description') as HTMLElement;

        const proofs: models.Proof[] = [];
        const shownProofs = new Set<string>();

        let productCount = 0;
        const objectLineItems = cartItem.quote?.quoteLineItems || soldOrder?.orderLineItems;
        if (objectLineItems) {
            for (const objectLineItem of objectLineItems) {
                if (objectLineItem.lineItemModel?.tax || objectLineItem.lineItemModel?.shipping) {
                    continue;
                }

                productCount++;

                if (productCount >= 2) {
                    break;
                }
            }
        }

        let invalidCustomQuantity = false;

        if (cartItem.quote) {
            title.textContent = `Quote ${cartItem.quote.serial || ''}`;
            if (cartItem.order) {
                const $reorderOf = document.createElement('span');
                $reorderOf.className = 'small text-muted ms-2';
                $reorderOf.textContent = `Reorder of ${cartItem.order.invoiceNumber}`;
                title.after($reorderOf);
            }

            if (cartItem.quote.quoteProofs) {
                for (const quoteProof of cartItem.quote.quoteProofs) {
                    if (!quoteProof.proof) {
                        continue;
                    }
                    quoteProof.proof.name = `Proof ${quoteProof.name || ''}`;
                    proofs.push(quoteProof.proof);
                }
            }

            if (cartItem.quote.quoteLineItems) {
                for (const quoteLineItem of cartItem.quote.quoteLineItems) {
                    if (quoteLineItem.lineItemModel?.tax || quoteLineItem.lineItemModel?.shipping) {
                        continue;
                    }
                    const { $, p } = await drawCartItemLine(quoteLineItem);
                    if (p) {
                        promises.push(p);
                    }
                    $cartItemDescription.appendChild($);

                    if (quoteLineItem.quoteProof?.proof?.id) {
                        shownProofs.add(quoteLineItem.quoteProof?.proof.id);
                    }

                    if (quoteLineItem.invalidCustomQuantity) {
                        invalidCustomQuantity = true;
                    }
                }
            }
        } else if (soldOrder) {
            title.textContent = `Reorder ${soldOrder.invoiceNumber || ''}`;

            if (soldOrder.orderProofs) {
                for (const orderProof of soldOrder.orderProofs) {
                    if (!orderProof.proof) {
                        continue;
                    }
                    orderProof.proof.name = `Proof ${strings.TrimSuffix(orderProof.name || '', filepath.Ext(orderProof.name || ''))}`;
                    proofs.push(orderProof.proof);
                }
            }

            if (soldOrder.orderLineItems?.length) {
                for (const orderLineItem of soldOrder.orderLineItems) {
                    if (orderLineItem.lineItemModel?.tax || orderLineItem.lineItemModel?.shipping) {
                        continue;
                    }
                    const { $, p } = await drawCartItemLine(orderLineItem);
                    if (p) {
                        promises.push(p);
                    }
                    $cartItemDescription.appendChild($);

                    if (orderLineItem.orderProof?.proof?.id) {
                        shownProofs.add(orderLineItem.orderProof?.proof.id);
                    }

                    if (orderLineItem.invalidCustomQuantity) {
                        invalidCustomQuantity = true;
                    }
                }
            }
        } else if (cartItem.stockItem) {
            const absoluteURLMatch = new RegExp('^https?://', 'i');
            if (absoluteURLMatch.exec(cartItem.stockItem.permalink)) {
                title.innerHTML = `<a href="${html.Escape(cartItem.stockItem.permalink)}">${html.Escape(cartItem.stockItem.name || '')}</a>`;
            } else {
                title.textContent = cartItem.stockItem.name || '';
            }

            if (cartItem.stockItem.stockItemImage) {
                const cartItemImage = $cartItem.querySelector<HTMLElement>('.cart-item-image');
                if (cartItemImage) {
                    cartItemImage.classList.remove('d-none');
                    const a = cartItemImage.querySelector('a');
                    if (a) {
                        a.href = cartItem.stockItem.stockItemImage.main || '#';
                        a.setAttribute('data-fancybox', `cart-item-${cartItem.id || ''}`);
                        a.setAttribute('data-caption', cartItem.stockItem.name || '');
                    }
                    const img = cartItemImage.querySelector('img');
                    if (img) {
                        img.src = cartItem.stockItem.stockItemImage.thumbnail || '';
                        img.alt = cartItem.stockItem.name || '';
                        img.title = cartItem.stockItem.name || '';
                    }
                }
            }

            if (cartItem.customizations.size) {
                for (const [key, value] of cartItem.customizations) {
                    const customization = template.Clone('.cart-item-customization-template', $cartItem).firstElementChild as HTMLElement;
                    const keyEl = customization.querySelector<HTMLElement>('.cart-item-customization-key');
                    if (keyEl) {
                        keyEl.textContent = key;
                    }

                    const valueEl = customization.querySelector<HTMLElement>('.cart-item-customization-value');
                    if (valueEl) {
                        valueEl.textContent = value;
                    }

                    $cartItemDescription.appendChild(customization);
                }
            }

            const qtyContainer = $cartItem.querySelector<HTMLElement>('.cart-item-quantity-container');

            const qty = qtyContainer?.querySelector<HTMLElement>('.cart-item-quantity');
            if (qty) {
                qty.textContent = new Intl.NumberFormat().format(cartItem.quantity || 0);
            }

            const qtyEdit = qtyContainer?.querySelector<HTMLAnchorElement>('.cart-item-quantity-edit');
            qtyEdit?.addEventListener('click', async e => {
                e.preventDefault();

                const oldQty = cartItem.quantity || 1;
                const tiers: carts.QuantityTier[] = cartItem.quantityPricing.size ?
                    [...cartItem.quantityPricing.entries()].map(([quantity, unitPrice]) => ({ quantity, unitPrice, fixedPrice: Big(0) })) :
                    [{ quantity: 1, unitPrice: cartItem.stockItem?.price ?? Big(0), fixedPrice: Big(0) }];
                const newQty = await carts.PromptQuantityEdit(oldQty || 1, tiers);

                void UpdateCartItems([{ id: cartItem.id || '', quantity: newQty }]).then(changed => {
                    carts.SetCartCount(changed.cartItemsCount);
                    toast.Success('Quantity updated');
                    void drawCart();
                }).catch(err => {
                    ShowError(err);

                    cartItem.quantity = oldQty;
                    void drawCart();
                });
            });
        } else if (cartItem.upload) {
            const name = cartItem.uploadLineItem?.name || cartItem.upload?.name || 'Custom Design';

            title.textContent = name;
            if (cartItem.order) {
                const $reorderOf = document.createElement('span');
                $reorderOf.className = 'd-block small fw-normal text-muted';
                $reorderOf.textContent = `Reorder of ${cartItem.order.invoiceNumber}`;
                title.append($reorderOf);
            }

            const $editDesign = $cartItem.querySelector<HTMLAnchorElement>('.cart-item-edit-design');
            if ($editDesign) {
                // Route to the product's own builder — lapel pins disambiguate by SKU
                const builderPath = BuilderPath(cartItem.uploadLineItem?.lineItemID, cartItem.uploadLineItem?.networkSkuid);
                if (builderPath && cartItem.builderDesignID && cartItem.id) {
                    $editDesign.href = `${builderPath}?cartItem=${encodeURIComponent(cartItem.id)}`;
                    $editDesign.classList.remove('d-none');
                } else {
                    $editDesign.parentElement?.removeChild($editDesign);
                }
            }

            if (IsNoMinimumBuilderSKU(cartItem.uploadLineItem?.networkSkuid)) {
                $cartItem.querySelector('.cart-item-production-note')?.classList.remove('d-none');
            }

            const orderLineItem = uploads.CartItemOrderLineItems(cartItem)[0];

            const { $, p } = await drawCartItemLine(orderLineItem);
            if (p) {
                promises.push(p);
            }
            $cartItemDescription.appendChild($);

            const $cartItemImage = $cartItem.querySelector<HTMLElement>('.cart-item-image');
            if ($cartItemImage) {
                $cartItemImage.classList.remove('d-none');
                const $a = $cartItemImage.querySelector('a');
                if ($a) {
                    $a.href = `${window.cloudFront}/${cartItem.upload.key ?? ''}`;
                    $a.setAttribute('data-fancybox', `cart-item-${cartItem.id || ''}`);
                    $a.setAttribute('data-caption', 'Custom Design');
                }
                const $img = $cartItemImage.querySelector('img');
                if ($img) {
                    void uploads.Load($img, `${window.cloudFront}/${cartItem.upload.key ?? ''}`, name, cartItem.upload.size ?? 0);
                }
            }

            if (cartItem.options?.length) {
                for (const opt of cartItem.options) {
                    const option = template.Clone('.cart-item-customization-template', $cartItem).firstElementChild as HTMLElement;
                    const $key = option.querySelector<HTMLElement>('.cart-item-customization-key');
                    if ($key) {
                        $key.textContent = opt.name ?? '';
                    }

                    const $value = option.querySelector<HTMLElement>('.cart-item-customization-value');
                    if ($value) {
                        $value.textContent = opt.value ?? '';
                    }

                    $cartItemDescription.appendChild(option);
                }
            }

            const qtyContainer = $cartItem.querySelector<HTMLElement>('.cart-item-quantity-container');

            const qty = qtyContainer?.querySelector<HTMLElement>('.cart-item-quantity');
            if (qty) {
                qty.textContent = new Intl.NumberFormat().format(cartItem.quantity || 0);
            }

            const qtyEdit = qtyContainer?.querySelector<HTMLAnchorElement>('.cart-item-quantity-edit');
            qtyEdit?.addEventListener('click', async e => {
                e.preventDefault();

                const oldQty = cartItem.quantity || 1;
                const tiers: carts.QuantityTier[] = cartItem.quantityPricing.size ?
                    [...cartItem.quantityPricing.entries()].map(([quantity, unitPrice]) => ({ quantity, unitPrice, fixedPrice: Big(0) })) :
                    [{
                        quantity: cartItem.quantity || 1,
                        unitPrice: Big(cartItem.total ?? '0').div(cartItem.quantity || 1),
                        fixedPrice: Big(0),
                    }];
                const newQty = await carts.PromptQuantityEdit(oldQty || 1, tiers);

                void UpdateCartItems([{ id: cartItem.id || '', quantity: newQty }]).then(changed => {
                    carts.SetCartCount(changed.cartItemsCount);
                    toast.Success('Quantity updated');
                    void drawCart();
                }).catch(err => {
                    ShowError(err);

                    cartItem.quantity = oldQty;
                    void drawCart();
                });
            });
        }

        if (cartItem.quote || soldOrder) {
            const entityLineItems: (models.QuoteLineItem | models.OrderLineItem)[] = [];
            if (cartItem.quote?.quoteLineItems?.length) {
                entityLineItems.push(...cartItem.quote.quoteLineItems);
            } else if (soldOrder?.orderLineItems?.length) {
                entityLineItems.push(...soldOrder.orderLineItems);
            }
            const productLineItems = entityLineItems?.filter(lineItem => lineItem.lineItemModel?.product === true);
            if (productLineItems?.length === 1 &&
                productLineItems[0].lineItemModel?.id === models.LineItemID.LINE_ITEMS_CHALLENGE_COIN &&
                productLineItems[0].lineItemType?.id &&
                productLineItems[0].lineItemTypeSize?.id) {
                let proof: models.Proof | undefined;
                let proofName: string;
                if (cartItem.quote) {
                    proof = cartItem.quote?.quoteLineItems?.[0].quoteProof?.proof;
                    proofName = `Proof ${cartItem.quote?.quoteLineItems?.[0].quoteProof?.number || 1}`;
                } else {
                    proof = soldOrder?.orderLineItems?.[0].orderProof?.proof;
                    proofName = soldOrder?.orderLineItems?.[0].orderProof?.name ?? '';
                }

                if (proof) {
                    const $bigAddParent = $cartItem.querySelector('.cart-big-coin-add-to-cart-parent') as HTMLElement;
                    const $bigAddButton = $bigAddParent.querySelector('.cart-big-coin-add-to-cart') as HTMLAnchorElement;

                    // $bigAddParent.classList.remove('d-none');
                    $bigAddButton.addEventListener('click', async e => {
                        e.preventDefault();

                        // check if proof is already an upload
                        let uploadID = proof?.uploadID ?? proof?.upload?.id;
                        if (!uploadID) {
                            const newUploadID = await uploads.NewFromURL(
                                proof?.name ?? '',
                                proof?.image?.files?.original?.mime ?? proof?.upload?.mime ?? '',
                                proof?.main ?? '',
                            ).then(files => files[0].uploadID);
                            uploadID = newUploadID;
                        }

                        const childLineItems: (models.QuoteChildLineItem | models.OrderChildLineItem)[] = [];
                        if (cartItem.quote) {
                            childLineItems.push(...cartItem.quote.quoteLineItems?.[0].quoteChildLineItems || []);
                        } else if (soldOrder) {
                            childLineItems.push(...soldOrder.orderLineItems?.[0].orderChildLineItems || []);
                        }

                        void carts.AddCartItem({
                            uploadID,
                            quantity: 1,
                            uploadLineItem: {
                                name: `${proofName} - 10" Display Coin`,
                                lineItem: productLineItems[0].lineItemModel?.networkName() ?? productLineItems[0].lineItemModel?.name ?? '',
                                type: productLineItems[0].lineItemType?.networkName() ?? productLineItems[0].lineItemType?.name ?? '',
                                size: '10"',
                                childLineItems: childLineItems?.map(childLineItem => ({
                                    child: childLineItem.childLineItem?.networkName() ?? childLineItem.childLineItem?.name ?? '',
                                    option: childLineItem.childLineItemOption?.networkName() ?? childLineItem.childLineItemOption?.name ?? '',
                                })),
                            },
                        }, {
                            promptToCheckout: false,
                            hideCartLink: true,
                        }).then(() => {
                            void drawCart();
                            toast.Success('10" Display Coin added to cart!');
                        }).catch(ShowError);
                    });
                }
            }
        }

        const footerProofs = $cartItem.querySelector('.cart-item-footer-proofs') as HTMLElement;
        let footerHasProofs = false;

        for (const proof of proofs) {
            if (proof.id && shownProofs.has(proof.id)) {
                continue;
            }

            const proofLink = template.Clone('.cart-item-footer-proof', $cartItem).firstElementChild as HTMLAnchorElement;
            const proofImg = proofLink.querySelector('img') as HTMLImageElement;
            if (proof?.main) {
                proofLink.href = proof.main;
                proofLink.setAttribute('data-fancybox', `cart-item-${cartItem.id || ''}`);
                proofLink.setAttribute('data-caption', proof.name || '');

                proofImg.src = proof.thumbnail || proof.main;

                proofImg.title = proof.name || '';
                proofImg.alt = proof.name || '';
            } else {
                proofLink.parentElement?.removeChild(proofLink);
            }

            footerProofs.appendChild(proofLink);
            footerHasProofs = true;
        }

        if (cartItem.cartItemUploads?.length) {
            const uploadEls: HTMLElement[] = [];
            for (const cartItemUpload of cartItem.cartItemUploads) {
                const upload = cartItemUpload.upload;
                if (!upload) {
                    continue;
                }
                if (upload.name?.startsWith('.')) {
                    continue;
                }

                const a = template.Clone('.cart-item-footer-proof', $cartItem).firstElementChild as HTMLAnchorElement;
                a.setAttribute('data-name', upload.name || '');
                a.href = `${window.cloudFront}/${upload.key || ''}`;
                a.setAttribute('data-fancybox', `cart-item-${cartItem.id || ''}`);
                a.setAttribute('data-caption', upload.name || '');

                uploadEls.push(a);

                footerProofs.appendChild(a);
                footerHasProofs = true;
            }

            uploads.LoadAll(uploadEls);
        }

        if (!footerHasProofs) {
            footerProofs.parentElement?.removeChild(footerProofs);
        }
        return { $: $cartItem, p: Promise.all(promises), invalidCustomQuantity };
    };

    // Only the most recent redraw may paint: an earlier GetCart that resolves
    // after a newer one started would repaint stale quantities and totals.
    let drawGeneration = 0;

    // Resolves true once the cart on screen matches the server; false when the
    // refresh failed (the error is already shown) and the totals may be stale.
    const drawCart = async (): Promise<boolean> => {
        const generation = ++drawGeneration;
        const subtotal = document.querySelectorAll('.cart-subtotal');
        const prepaymentParents = document.querySelectorAll('.cart-prepayment-parent');
        const prepaymentEls = document.querySelectorAll('.cart-prepayment');
        const availableDiscountParents = document.querySelectorAll('.cart-available-discount-parent');
        const availableDiscountEls = document.querySelectorAll('.cart-available-discount');

        if (!GetCartID()) {
            showEmptyCart();
            return true;
        }

        return carts.GetCart('cartItems,totals').then(async cart => {
            if (generation !== drawGeneration) {
                return true;
            }
            // total comes back net of any project deposit. Show the gross
            // amount as the subtotal so it visibly adds up against the item
            // cards, and break the deposit out as its own credit line —
            // checkout shows the same line in its full breakdown.
            const prepayment = cart?.totals.prepayment ?? Big(0);
            const availableDiscount = cart?.totals.availableDiscount ?? Big(0);
            // total is net of the available-discount spend too, so add it back for the pre-credit subtotal.
            subtotal.forEach(el => el.textContent = DollarFormat((cart?.totals.total ?? Big(0)).add(prepayment).sub(availableDiscount)));
            if (prepayment.gt(0)) {
                prepaymentEls.forEach(el => el.textContent = DollarFormat(Big(0).sub(prepayment)));
                prepaymentParents.forEach(el => el.classList.remove('d-none'));
            } else {
                prepaymentParents.forEach(el => el.classList.add('d-none'));
            }
            if (availableDiscount.eq(0)) {
                availableDiscountParents.forEach(el => el.classList.add('d-none'));
            } else {
                availableDiscountEls.forEach(el => el.textContent = DollarFormat(availableDiscount));
                availableDiscountParents.forEach(el => el.classList.remove('d-none'));
            }

            if (!cart?.cartItems.length) {
                if (generation !== drawGeneration) {
                    return true;
                }
                showEmptyCart();
                return true;
            }
            spinner.parentElement?.removeChild(spinner);
            const frag = document.createDocumentFragment();

            const qtyBatchUpdate = new Batch();

            let i = 0;
            const promises: Promise<unknown>[] = [];
            // Checkout is gated on the whole cart, so it is decided here from
            // every item and applied with the paint — never per item, never
            // by a stale redraw.
            let checkoutBlocked = false;
            for (const cartItem of cart.cartItems) {
                if (i++) {
                    const hr = document.createElement('hr') as HTMLElement;
                    hr.classList.add('m-0');
                    frag.appendChild(hr);
                }
                const { $, p, invalidCustomQuantity } = await drawCartItem(cartItem);
                frag.appendChild($);
                if (p) {
                    promises.push(p);
                }
                if (invalidCustomQuantity) {
                    checkoutBlocked = true;
                }
            }
            if (generation !== drawGeneration) {
                return true;
            }
            cartCard.innerHTML = '';
            cartCard.appendChild(frag);
            if (checkoutBlocked) {
                checkoutButton.setAttribute('disabled', 'disabled');
            } else {
                checkoutButton.removeAttribute('disabled');
            }
            void Promise.all(promises).then(() => qtyBatchUpdate.exec());
            return true;
        }).catch((err: unknown) => {
            if (generation !== drawGeneration) {
                return true;
            }
            if ((err as AxiosError).response?.status === 404) {
                showEmptyCart();
                return true;
            }
            ShowError(err);
            return false;
        });
    };

    void drawCart();
}